import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import {
  authKeys,
  departmentKeys,
  initializeKeys,
  orgFunctionKeys,
  organizationKeys,
  orgMemberKeys
} from '../query-keys';
import {
  initializeAssignUserPlacementContract,
  type InitializeAssignUserPlacementRequest,
  type InitializeAssignUserPlacementResponse
} from '@bliks-io/contracts';

/**
 * Assign user placement mutation (setup wizard).
 *
 * Places an existing internal user — typically a protected member the
 * add-users pipeline may not touch (superadmin-precreated, invited or
 * activated) — into a department, with an optional role. Identity fields are
 * structurally out of reach: the PATCH schema carries none.
 */
export function useInitializeAssignUserPlacement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: InitializeAssignUserPlacementRequest;
    }): Promise<InitializeAssignUserPlacementResponse> => {
      const path = initializeAssignUserPlacementContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<InitializeAssignUserPlacementResponse>(
        path,
        data
      );
      return response;
    },
    // Awaited (returned) on purpose: placement re-runs manager resolution
    // server-side and feeds the summary's without-department count — the
    // wizard derives step gating and the overview alert from these caches.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: orgMemberKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: initializeKeys.unresolvedDepartments() }),
        queryClient.invalidateQueries({ queryKey: departmentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: initializeKeys.summary() }),
        // Placement may find-or-create a role OrgFunction (with description)
        // and GC the orphaned previous one — the Funktionen step and the
        // role-description seed join read this cache.
        queryClient.invalidateQueries({ queryKey: orgFunctionKeys.all }),
        // A first placement stamps initialization.users — navigation after
        // mutateAsync must see the refetched progress or it clamps back to
        // the Mitarbeiter step.
        queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
        // The routing gate reads setup state from the profile cache.
        queryClient.invalidateQueries({ queryKey: authKeys.profile() })
      ])
  });
}
